import type { PostDestinationId } from '../../lib/publishing/destinations';
import { DESTINATION_ORDER } from '../../lib/publishing/destinations';
import { applyStoryGroupChange, bothStoriesEnabled, type ComposerTarget } from './composerState';
import { DestinationOptionsCard } from './DestinationOptionsCard';

// Publishing composer §D — one options block per ticked destination, with the two
// STORY destinations CONSOLIDATED (item 2, 2026-07-28).
//
// FB Story and IG Story always share the same asset, so when BOTH are ticked this
// renders ONE combined "Story" block whose media / caption / first-comment
// override writes to BOTH story targets in lockstep (applyStoryGroupChange). The
// two destination ticks and the two underlying targets are unchanged — the post
// still carries a FB-story target and an IG-story target — this is purely a UI
// consolidation so the AM sets the story asset once. A single story ticked shows
// its own block as normal.

export function DestinationOptionsSection({
  targets,
  baseCaption,
  disabled,
  onChange,
}: {
  targets: Record<PostDestinationId, ComposerTarget>;
  // The main caption — a caption override prefills with it when ticked.
  baseCaption: string;
  disabled?: boolean;
  onChange: (targets: Record<PostDestinationId, ComposerTarget>) => void;
}) {
  const grouped = bothStoriesEnabled(targets);

  const setOne = (dest: PostDestinationId, next: ComposerTarget) =>
    onChange({ ...targets, [dest]: next });

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">Destination options</label>
      {DESTINATION_ORDER.filter((dest) => targets[dest].enabled)
        // When both stories are ticked the combined block replaces them: render it
        // in the FB-story slot and skip the IG-story slot.
        .filter((dest) => !(grouped && dest === 'instagram_story'))
        .map((dest) => {
          if (grouped && dest === 'facebook_story') {
            return (
              <DestinationOptionsCard
                key="story-combined"
                target={targets.facebook_story}
                title="Story"
                iconChannels={['facebook', 'instagram']}
                baseCaption={baseCaption}
                disabled={disabled}
                onChange={(next) => onChange(applyStoryGroupChange(targets, next))}
              />
            );
          }
          return (
            <DestinationOptionsCard
              key={dest}
              target={targets[dest]}
              baseCaption={baseCaption}
              disabled={disabled}
              onChange={(next) => setOne(dest, next)}
            />
          );
        })}
    </div>
  );
}
